/**
 * Getting a meeting's audio into the system — the two doors that matter.
 *
 * Upload a file, or press Record with the laptop on the boardroom table. Both
 * hand the same bytes to the same endpoint; the backend only records which it
 * was. That is deliberate: everything downstream — transcription, naming the
 * speakers, drafting — is identical either way, so this component is the only
 * place in the product that knows there is more than one way in.
 *
 * The recorder is intentionally plain. It shows that it is recording, how long
 * it has been going, and nothing else. A board meeting runs for two hours with
 * the screen ignored; a waveform would only be something else to go wrong.
 *
 * What it will NOT do is capture another application's audio. A browser tab
 * cannot reach the Zoom or Teams desktop app, so for a fully-online meeting this
 * records only what the room microphone hears — which for a remote participant
 * is close to nothing. The panel says so rather than letting someone discover it
 * after the meeting.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { TbUpload, TbMicrophone, TbPlayerStopFilled, TbAlertTriangle } from 'react-icons/tb';
import Button from '../common/Button';

const MAX_MB = 500;

const clock = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};

/** The container the browser will actually give us, best first. */
const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || '';
};

export default function RecordingPanel({ meeting, transcription, busy, onSubmit }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState('');
  // Required before we record. Not required for an upload: that file was
  // captured by somebody else on their own terms, and claiming their room was
  // informed would be inventing a fact about a meeting we were not at.
  const [consent, setConsent] = useState(false);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const fileRef = useRef(null);

  const canRecord = typeof navigator !== 'undefined'
    && navigator.mediaDevices?.getUserMedia
    && typeof MediaRecorder !== 'undefined';

  /** Release the microphone. Leaving it open keeps the browser's tab indicator lit. */
  const teardown = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  // A recording in progress when the component goes away would otherwise hold
  // the microphone for the life of the tab.
  useEffect(() => teardown, [teardown]);

  const startRecording = async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      streamRef.current = stream;
      chunksRef.current = [];

      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data?.size) chunksRef.current.push(event.data);
      };

      recorder.onstop = () => {
        const type = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        chunksRef.current = [];
        teardown();
        setRecording(false);

        // A recording with nothing in it is a failed capture, not a short
        // meeting — sending it would waste a transcription call to be told so.
        if (!blob.size) {
          setError('Nothing was recorded. Check that the microphone is working and try again.');
          return;
        }

        const ext = type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm';
        onSubmit(new File([blob], `meeting-recording.${ext}`, { type }), 'in_app', true);
      };

      // Emit a chunk a minute. Without a timeslice the whole recording is one
      // blob delivered at the end, so a two-hour meeting is held entirely in
      // memory before anything is written.
      recorder.start(60000);
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((n) => n + 1), 1000);
    } catch (err) {
      teardown();
      setError(
        err.name === 'NotAllowedError'
          ? 'Microphone access was refused. Allow it in your browser and try again.'
          : `The microphone could not be started: ${err.message}`,
      );
    }
  };

  const stopRecording = () => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
  };

  const handleFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (file.size > MAX_MB * 1024 * 1024) {
      setError(`That file is ${Math.round(file.size / 1024 / 1024)} MB. The limit is ${MAX_MB} MB.`);
      return;
    }
    setError('');
    onSubmit(file, 'upload');
  };

  const unavailable = transcription && transcription.available === false;

  return (
    <div className="rounded-xl border-[1.5px] border-[#ddd6c6] bg-white p-5">
      <h3 className="text-[15px] font-bold text-[#2c2a24] mb-1">Add the recording</h3>
      <p className="text-[12px] text-[#6b6252] mb-4">
        Upload a file recorded on any device, or record here with the laptop on the table.
      </p>

      {unavailable && (
        <div className="mb-4 flex gap-2 rounded-lg border border-[#e3c9a0] bg-[#fdf6e9] px-3 py-2.5">
          <TbAlertTriangle className="mt-0.5 shrink-0 text-[#9a5b1e]" size={16} />
          <p className="text-[12px] text-[#7a5a2a]">
            Transcription is not configured, so a recording cannot be processed yet. {transcription.reason}
          </p>
        </div>
      )}

      {/* Whisper and its like return one speaker for the whole meeting. Saying so
          here is the difference between a surprise and an expectation — SS-1
          needs dissent named, and that will not be possible. */}
      {transcription?.available && transcription.diarizes === false && (
        <div className="mb-4 flex gap-2 rounded-lg border border-[#e3c9a0] bg-[#fdf6e9] px-3 py-2.5">
          <TbAlertTriangle className="mt-0.5 shrink-0 text-[#9a5b1e]" size={16} />
          <p className="text-[12px] text-[#7a5a2a]">
            The configured transcription service cannot tell speakers apart, so the whole
            transcript will come back under one name and nothing can be attributed to an
            individual director — including dissent.
          </p>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-[#e0b4b4] bg-[#fdf1f1] px-3 py-2.5 text-[12px] text-[#8a3a3a]">
          {error}
        </div>
      )}

      {recording ? (
        <div className="flex flex-wrap items-center gap-4 rounded-lg border-[1.5px] border-[#c4503f] bg-[#fdf3f1] px-4 py-3">
          <span className="flex items-center gap-2 text-[13px] font-bold text-[#a83c2c]">
            <span className="inline-block h-2.5 w-2.5 animate-pulse rounded-full bg-[#c4503f]" />
            Recording
          </span>
          <span className="font-mono text-[15px] tabular-nums text-[#2c2a24]">{clock(elapsed)}</span>
          <Button variant="danger" size="sm" icon={TbPlayerStopFilled} onClick={stopRecording}>
            Stop and send
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-3">
          {/* A hidden input driven by the real Button, rather than a <label>
              dressed up as one — that lost Button's hover, focus ring and
              disabled handling, and drifted from every other action in the app. */}
          <input
            ref={fileRef}
            type="file"
            accept="audio/*,video/webm,video/mp4"
            className="sr-only"
            onChange={handleFile}
            disabled={busy || unavailable}
          />
          <Button
            variant="primary"
            icon={TbUpload}
            loading={busy}
            disabled={busy || unavailable}
            onClick={() => fileRef.current?.click()}
          >
            Upload a recording
          </Button>

          <Button
            variant="ink"
            icon={TbMicrophone}
            onClick={startRecording}
            disabled={busy || unavailable || !canRecord || !consent}
            title={canRecord
              ? (consent ? undefined : 'Confirm the meeting has been told it is being recorded')
              : 'This browser cannot record audio.'}
          >
            Record here
          </Button>

          {/* Recording people without telling them is the one duty in this flow
              that is ours rather than the client's — we are the ones capturing
              it. Stored on the meeting with who confirmed it and when. Not asked
              for an upload: that file was recorded by somebody else, and
              claiming their room was informed would be inventing a fact about a
              meeting we were not at. */}
          <label className="flex w-full cursor-pointer items-start gap-2 text-[12px] leading-snug text-[#6b6252]">
            <input
              type="checkbox"
              className="mt-0.5 shrink-0"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              disabled={busy || unavailable}
            />
            I confirm everyone at this meeting has been told it is being recorded.
          </label>
        </div>
      )}

      {meeting.mode === 'video' && (
        <p className="mt-3 text-[11px] leading-relaxed text-[#8a8172]">
          This meeting is online. Recording here captures only what this device&rsquo;s microphone
          hears — a browser cannot capture the Zoom or Teams app&rsquo;s audio. For a fully remote
          meeting, record in the meeting platform and upload the file instead.
        </p>
      )}
    </div>
  );
}
